/**
 * The location detail screen — the wide `admin`/`manager`-only surface, with the edit form plus the
 * two nested panels the legacy dashboard's own `locations.tsx` nested inside a location's detail
 * view: Terminales and Usuarios OTC. See the module-level decisions in the task history for why
 * YubiKeys is a separate top-level screen instead of a third panel here.
 */
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { useManagedLocation, useManagedMaquinas, useManagedOtcUsers } from './hooks.ts';
import { LocationEditForm } from './LocationEditForm.tsx';
import { MaquinasPanel } from './MaquinasPanel.tsx';
import { OtcUsersPanel } from './OtcUsersPanel.tsx';
import { locationsRoutePath } from './paths.ts';

const failureMessage = (failure: ApiFailure): string => {
  if (failure.kind === 'http' && failure.status === 403) {
    return 'Tu rol no tiene permiso para ver esta localidad.';
  }
  if (failure.kind === 'http' && failure.status === 404) return 'Esa localidad no existe.';
  if (failure.kind === 'malformed') {
    return 'La respuesta no cumple el contrato esperado.';
  }
  return 'No se pudo cargar la localidad.';
};

const idFrom = (value: string | undefined): number | null =>
  value !== undefined && /^[1-9]\d*$/.test(value) ? Number(value) : null;

const InvalidLocationId = (): ReactElement => (
  <section>
    <h2>Localidad no encontrada</h2>
    <p role="alert">La dirección no corresponde a una localidad.</p>
  </section>
);

const KnownLocationDetail = ({ locationId }: { readonly locationId: number }): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const csrfToken = authenticated?.csrfToken ?? '';
  const managedLocation = useManagedLocation(
    session.client,
    locationId,
    csrfToken,
    session.handleFailure,
  );
  const managedMaquinas = useManagedMaquinas(
    session.client,
    locationId,
    csrfToken,
    session.handleFailure,
  );
  const managedOtcUsers = useManagedOtcUsers(
    session.client,
    locationId,
    csrfToken,
    session.handleFailure,
  );

  return (
    <section aria-labelledby="location-detail">
      <Link to={locationsRoutePath}>Volver a localidades</Link>

      {managedLocation.state.status === 'loading' ? (
        <>
          <h2 id="location-detail">Localidad</h2>
          <p role="status">Cargando localidad…</p>
        </>
      ) : managedLocation.state.status === 'error' ? (
        <>
          <h2 id="location-detail">Localidad</h2>
          <p role="alert">{failureMessage(managedLocation.state.failure)}</p>
        </>
      ) : (
        <>
          <h2 className="mt-4" id="location-detail">
            {managedLocation.state.data.nombre}
          </h2>
          <div className="mt-4 grid gap-6">
            <LocationEditForm detail={managedLocation.state.data} managed={managedLocation} />
            <MaquinasPanel managed={managedMaquinas} />
            <OtcUsersPanel managed={managedOtcUsers} />
          </div>
        </>
      )}
    </section>
  );
};

export const LocationDetailPage = (): ReactElement => {
  const locationId = idFrom(useParams<{ id: string }>().id);
  if (locationId === null) return <InvalidLocationId />;
  return <KnownLocationDetail locationId={locationId} />;
};
